import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { TeamMemberData } from 'src/replicant/team/team.ruleset';

type TeamMemberInfoEntryOpts = ViewOpts & {
  onEntryClick?: (index: number) => Promise<void>;
};

export class TeamMemberInfoEntry extends ButtonScaleView {
  private readonly bg: ImageScaleView;
  private readonly position: { update: (team: TeamMemberData) => void };
  private readonly avatar: Avatar;
  private readonly name: { update: (team: TeamMemberData) => void };
  private readonly level: { update: (team: TeamMemberData) => void };
  private data!: TeamMemberData;

  constructor(opts: TeamMemberInfoEntryOpts) {
    super({
      width: 553,
      height: 75,
      ...opts,
      onClick: async () => opts.onEntryClick?.(this.data.position - 1),
      tracking: {
        feature: Feature.TEAM._,
        $subFeature: Feature.TEAM.MEMBER_ENTRY,
      },
    });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    const layout = new FlexBoxView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      flex: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    this.position = this.createPositionView(layout);

    this.avatar = new Avatar({
      superview: layout,
      width: 54,
      height: 54,
      centerOnOrigin: false,
    });

    this.avatar.updateOpts({
      offsetY: -3,
      offsetX: 7,
    });

    this.name = this.createNameView(layout);
    this.level = this.createLevelView(layout);
  }

  /**
   * Create position view
   */
  private createPositionView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 70,
      height: this.style.height,
      flex: {
        marginTop: 4,
        marginLeft: 3,
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
    });

    const text = new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'center',
      size: 34,
      font: 'Title',
      width: 65,
      height: 61,
    });

    const icon = new ImageScaleView({
      superview: layout,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 65,
      height: 61,
    });

    return {
      update: (team: TeamMemberData) => {
        if (team.position >= 1 && team.position <= 3) {
          icon.updateOpts({
            image: `assets/ui/match2/tournament/star_tournament_medal_${team.position}.png`,
            visible: true,
          });
          text.updateOpts({ visible: false });
        } else {
          text.updateOpts({ visible: true });
          icon.updateOpts({ visible: false });
          text.localeText = () => team.position.toString();
        }
      },
    };
  }

  /**
   * Create name view
   */
  private createNameView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 280,
      height: this.style.height,
      flex: {
        top: -3,
        marginLeft: 20,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    const name = new LangBitmapFontTextView({
      superview: layout,
      align: 'left',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: 280,
      height: this.style.height,
      color: '#662420',
    });

    return {
      update: (team: TeamMemberData) => {
        name.localeText = () => team.profile.name;
        name.updateOpts(
          team.me
            ? {
                font: 'Title',
                color: '#ffffff',
              }
            : {
                font: 'Body',
                color: '#78263E',
              },
        );
      },
    };
  }

  /**
   * Create level view
   */
  private createLevelView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 105,
      height: superview.style.height - 20,
      flex: {
        top: -8,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignContent: 'flex-start',
      },
    });

    new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: layout.style.width,
      height: superview.style.height * 0.5,
      color: '#BF8147',
      localeText: () => i18n('profile.level'),
    });

    const level = new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Title',
      width: layout.style.width,
      height: superview.style.height * 0.5,
      color: '#ffffff',
      localeText: () => `0`,
    });

    return {
      update: (team: TeamMemberData) => {
        level.localeText = () => team.level.toString();
      },
    };
  }

  /**
   * Update view with data
   */
  public setup(params: TeamMemberData) {
    this.data = params;

    this.position.update(params);
    this.level.update(params);
    this.name.update(params);

    this.avatar.updateImage(params.profile.avatar);
    this.avatar.setFrame(params.profile.frame);

    this.bg.updateOpts({
      image: params.me
        ? 'assets/ui/match2/team/frames/panel_list_orange.png'
        : 'assets/ui/match2/common/list_entry.png',
    });
  }
}
